import type { VoidLogEntry } from '@/types'

/**
 * Pure helpers for the Void Log page (F-FE-N138). Kept out of the component so
 * the reading of a void row — what it cost, who approved it, how the stored
 * reason splits into category and note — is testable and reused by the table,
 * the detail panel and the CSV export.
 */

/** Rupees lost on one void row. */
export function voidValue(entry: Pick<VoidLogEntry, 'unit_price' | 'quantity'>): number {
  return entry.unit_price * entry.quantity
}

/**
 * Split a stored reason into its category and the server's note.
 * "Other: guest spilled the tray" → { category: 'Other', note: 'guest spilled the tray' }.
 * Reasons are stored with the note appended after ": " (F-CTRL-N45); every
 * other reason is bare. An empty reason reads as "No reason".
 */
export function splitReason(reason: string | null | undefined): { category: string; note?: string } {
  const raw = (reason ?? '').trim()
  if (!raw) return { category: 'No reason' }
  const idx = raw.indexOf(':')
  if (idx < 0) return { category: raw }
  const note = raw.slice(idx + 1).trim()
  const category = raw.slice(0, idx).trim() || 'No reason'
  return note ? { category, note } : { category }
}

/**
 * A void approved by the same PIN that made it. Rows with no recorded voider
 * are never "self-authorized" — there is nobody to compare.
 */
export function isSelfAuthorized(
  entry: Pick<VoidLogEntry, 'voided_by' | 'authorized_by'>,
): boolean {
  return !!entry.voided_by && entry.voided_by === entry.authorized_by
}

/**
 * How long after the order was created the void happened, in business terms.
 * Same calendar day → "same day"; otherwise "N days after the order". A void
 * weeks after the order is the pattern a manager wants to notice.
 */
/**
 * Which business day an instant falls in, as a comparable local-midnight key.
 * Shifting the instant back by the boundary hour before taking the calendar
 * day is the same transform `useReportRange`'s `businessToday` uses.
 */
function businessDayKey(d: Date, boundaryHour: number): number {
  const shifted = new Date(d.getTime() - boundaryHour * 3_600_000)
  return new Date(shifted.getFullYear(), shifted.getMonth(), shifted.getDate()).getTime()
}

/**
 * F-FE-N148: counts BUSINESS days, so a restaurant trading past midnight is
 * not shown voiding bills "1 day after the order" half an hour later. The
 * caller must pass the tenant's `day_boundary_hour` (`useDayBoundaryHour()`) —
 * deliberately required rather than defaulted, so a second copy of the default
 * cannot drift from the hook's.
 */
export function daysAfterLabel(
  orderCreatedAt: string | Date,
  voidedAt: string | Date,
  boundaryHour: number,
): string {
  const a = new Date(orderCreatedAt)
  const b = new Date(voidedAt)
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return ''
  const dayA = businessDayKey(a, boundaryHour)
  const dayB = businessDayKey(b, boundaryHour)
  // Round, not floor: a DST shift makes the gap 23h or 25h.
  const days = Math.round((dayB - dayA) / 86_400_000)
  if (days <= 0) return 'same day'
  if (days === 1) return '1 day after the order'
  return `${days} days after the order`
}

/** "dine_in" → "Dine-in", "takeout" → "Takeout". */
export function orderTypeLabel(orderType: string | null | undefined): string {
  if (!orderType) return ''
  const t = orderType.replace(/_/g, '-')
  return t.charAt(0).toUpperCase() + t.slice(1)
}
